/**
 * The quotes module's URL adapter (docs/audits/PILOT-QUOTES-SHELL.md §1.1, §1.3).
 *
 * The URL is the source of truth for which quote is open and how the list is
 * filtered. QuotationManager keeps its `view` enum as a render detail, but only
 * ever writes it from what this hook reads; everything that used to call
 * setView() calls one of the writers below instead.
 *
 *   /quotes                 the list (list state in the query, defaults omitted)
 *   /quotes/:id             that quote's record; the list query rides along
 *   /quotes/:id/edit        the builder, editing it
 *   /quotes/new             the builder, new; ?customerId= ?leadId= ?draft= prefill
 *
 * History (decision 3): list → record pushes; record → record replaces, so Back
 * always returns to the list in one step. Filters, search and sort replace;
 * view, page and tab push, so Back steps through them.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { usePreferences } from '../../contexts/PreferencesContext';
import { shellScrollTo, shellScrollTop } from '../layout/shellScroll';

export const QUOTE_VIEWS = [
  'all', 'draft', 'sent', 'pending_approval', 'won', 'lost',
  'expiring', 'high_value', 'recent', 'no_customer', 'mine', 'stale'
];
export const QUOTE_TABS = ['approvals', 'followups', 'analytics', 'dashboard'];

const SORTS = ['date', 'value', 'customer', 'status'];
const DIRS = ['asc', 'desc'];
const DATES = ['all', 'today', 'week', 'month'];
const VALUES = ['all', '0-1000', '1000-5000', '5000-10000', '10000+'];
const LAYOUTS = ['list', 'kanban'];

/**
 * Keys that describe the list. They ride along onto record and builder URLs; every other
 * key (leadId, draft, focus) belongs to one visit and is dropped on the next write.
 */
const LIST_KEYS = ['view', 'q', 'sort', 'dir', 'page', 'date', 'value', 'expiring', 'layout', 'customerId', 'tab'];

/** The built-in list defaults; a user's own come from quotes.list (S4b, §5.2). */
export const BASE_LIST_DEFAULTS = Object.freeze({
  view: 'all', q: '', sort: 'date', dir: 'desc', page: 1,
  date: 'all', value: 'all', expiring: false, layout: 'list', customerId: null
});

/**
 * The list defaults for a quotes document: its list.defaultView, layout, sort and dir over the
 * built-in ones. Defaults stay out of the URL, so a bare /quotes is the user's own default
 * view, and every other view (All included, when it is not the default) is explicit.
 * The device key quoteListViewMode is not read (DECISION 7).
 */
export function listDefaultsFrom(doc) {
  const list = (doc && doc.list) || {};
  const d = { ...BASE_LIST_DEFAULTS };
  if (QUOTE_VIEWS.includes(list.defaultView) && list.defaultView !== 'stale') d.view = list.defaultView;
  if (LAYOUTS.includes(list.layout)) d.layout = list.layout;
  if (SORTS.includes(list.sort)) d.sort = list.sort;
  if (DIRS.includes(list.dir)) d.dir = list.dir;
  return d;
}

/** This user's list defaults (S4b). */
export function useQuoteListDefaults() {
  const { doc } = usePreferences('quotes');
  return useMemo(() => listDefaultsFrom(doc), [doc]);
}

const positiveInt = (value) => {
  if (typeof value !== 'string' || !/^\d+$/.test(value)) return null;
  const n = parseInt(value, 10);
  return n > 0 ? n : null;
};

const oneOf = (value, allowed, fallback) => (allowed.includes(value) ? value : fallback);

function defaultsFor(defaults) {
  return defaults || BASE_LIST_DEFAULTS;
}

/**
 * Pure parse of a quotes location.
 * @param {string} pathname - e.g. '/quotes/12/edit'
 * @param {string} search - e.g. '?view=sent'
 * @param {object} [defaults] - the list defaults (listDefaultsFrom(doc)); the built-in ones if omitted
 */
export function parseQuoteLocation(pathname, search, defaults) {
  const rest = String(pathname || '').replace(/^\/quotes\/?/, '').replace(/\/+$/, '');
  const segments = rest ? rest.split('/') : [];

  let mode = 'list';
  let id = null;
  let invalidId = false;
  if (segments.length === 1 && segments[0] === 'new') {
    mode = 'new';
  } else if (segments.length >= 1) {
    const parsed = positiveInt(segments[0]);
    if (segments.length === 1 && parsed) {
      mode = 'record';
      id = parsed;
    } else if (segments.length === 2 && segments[1] === 'edit' && parsed) {
      mode = 'edit';
      id = parsed;
    } else {
      mode = 'record';
      invalidId = true;
    }
  }

  const sp = new URLSearchParams(search || '');
  const d = defaultsFor(defaults);
  const list = {
    view: oneOf(sp.get('view'), QUOTE_VIEWS, d.view),
    q: sp.get('q') || '',
    sort: oneOf(sp.get('sort'), SORTS, d.sort),
    dir: oneOf(sp.get('dir'), DIRS, d.dir),
    page: positiveInt(sp.get('page')) || 1,
    date: oneOf(sp.get('date'), DATES, d.date),
    value: oneOf(sp.get('value'), VALUES, d.value),
    expiring: sp.get('expiring') === '1',
    layout: oneOf(sp.get('layout'), LAYOUTS, d.layout),
    customerId: positiveInt(sp.get('customerId'))
  };

  return {
    mode,
    id,
    invalidId,
    list,
    tab: oneOf(sp.get('tab'), QUOTE_TABS, null),
    prefill: {
      customerId: positiveInt(sp.get('customerId')),
      leadId: positiveInt(sp.get('leadId')),
      draft: sp.get('draft') || null
    },
    focus: sp.get('focus') === 'search' ? 'search' : null
  };
}

/**
 * Legacy inbound forms, normalised once (§1.1). Returns the canonical location, or null
 * when the URL is already canonical. `?view=stale` goes where `?filter=stale` goes:
 * "stale" means the follow-ups screen (quickActionNav), and the list has no stale view.
 */
export function legacyQuoteRedirect(pathname, search) {
  const sp = new URLSearchParams(search || '');
  let target = String(pathname || '/quotes').replace(/\/+$/, '') || '/quotes';
  let changed = false;

  if (sp.get('view') === 'detail' && positiveInt(sp.get('id'))) {
    target = `/quotes/${positiveInt(sp.get('id'))}`;
    sp.delete('view');
    sp.delete('id');
    changed = true;
  }
  const filter = sp.get('filter');
  if (filter === 'expiring') {
    sp.delete('filter');
    sp.set('view', 'expiring');
    changed = true;
  } else if (filter === 'stale') {
    sp.delete('filter');
    sp.set('tab', 'followups');
    changed = true;
  }
  if (sp.get('view') === 'stale') {
    sp.delete('view');
    sp.set('tab', 'followups');
    changed = true;
  }
  if (sp.has('customer')) {
    const customer = positiveInt(sp.get('customer'));
    sp.delete('customer');
    if (customer) sp.set('customerId', String(customer));
    changed = true;
  }

  if (!changed) return null;
  const qs = sp.toString();
  return { pathname: target, search: qs ? `?${qs}` : '' };
}

/** Serialise a list-key value, or null when it is the default (defaults stay out of the URL). */
function encode(key, value, defaults) {
  const d = defaultsFor(defaults);
  if (value === null || value === undefined || value === '' || value === false) return null;
  if (key === 'expiring') return value ? '1' : null;
  if (key === 'page') return Number(value) > 1 ? String(Number(value)) : null;
  if (key in d && String(d[key]) === String(value)) return null;
  return String(value);
}

/**
 * The URL of a list view (S3's flyout and the list header's view menu, §4.1). Each view is a
 * link: `all` is the bare list, `stale` is the follow-ups screen (as `?view=stale` redirects),
 * the rest `?view=<key>`. A view replaces every other list filter, as a chip did (search,
 * date, value, expiring, customer); pass the current search to keep how the list is shown
 * (sort, dir, layout). A new view starts on page 1 and leaves any tab.
 */
const VIEW_KEEPS = ['sort', 'dir', 'layout'];

export function quoteViewHref(view, currentSearch = '', defaults) {
  const sp = new URLSearchParams(currentSearch);
  for (const key of [...sp.keys()]) {
    if (!VIEW_KEEPS.includes(key)) sp.delete(key);
  }
  if (view === 'stale') {
    sp.set('tab', 'followups');
  } else {
    const encoded = QUOTE_VIEWS.includes(view) ? encode('view', view, defaults) : null;
    if (encoded !== null) sp.set('view', encoded);
  }
  const qs = sp.toString();
  return `/quotes${qs ? `?${qs}` : ''}`;
}

const PUSH_KEYS = ['view', 'page', 'tab'];
/** Keys that change which rows the list holds or their order: a change resets `page`. */
const FILTER_KEYS = ['view', 'q', 'sort', 'dir', 'date', 'value', 'expiring', 'customerId'];

/**
 * The list's window scroll, by history entry. Saved when the list is left for a record or
 * the builder, restored when Back returns to that entry. Module-level: the history stack
 * outlives a remount of the page.
 */
const listScrollByKey = new Map();

export default function useQuoteRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();

  // The newest search string, updated synchronously by every writer, so two writes in
  // one tick (clear-all, a chip that also resets the advanced filters) both land.
  const searchRef = useRef(location.search);
  searchRef.current = location.search;

  // The list defaults are the user's (quotes.list in the server document, S4b), outside the
  // URL. When they change the parse re-reads them even if the URL stays the same.
  const quotesPrefs = usePreferences('quotes');
  const defaults = useMemo(() => listDefaultsFrom(quotesPrefs.doc), [quotesPrefs.doc]);
  const updatePrefs = quotesPrefs.update;

  const parsed = useMemo(
    () => parseQuoteLocation(location.pathname, location.search, defaults),
    [location.pathname, location.search, defaults]
  );

  // Scroll: a record or the builder opens at the top; Back to the list returns it where it
  // was. Query-only changes (filters, search, sort, layout) never move the page. The scroll is
  // the shell's <main> (viewport-locked, polish 4), or the window outside the shell.
  const lastPathRef = useRef(location.pathname);
  useEffect(() => {
    const pathChanged = lastPathRef.current !== location.pathname;
    lastPathRef.current = location.pathname;
    if (typeof window === 'undefined') return undefined;
    if (navigationType === 'POP') {
      if (parsed.mode !== 'list' || !listScrollByKey.has(location.key)) return undefined;
      const y = listScrollByKey.get(location.key);
      // After the list has rendered back in: the page sets its view from the route in an effect.
      const frame = window.requestAnimationFrame(() => shellScrollTo(y));
      return () => window.cancelAnimationFrame(frame);
    }
    if (pathChanged && parsed.mode !== 'list') shellScrollTo(0);
    return undefined;
  }, [location.key, location.pathname, navigationType, parsed.mode]);

  // Legacy forms: rewritten once, with replace, so they never enter history.
  useEffect(() => {
    const to = legacyQuoteRedirect(location.pathname, location.search);
    if (to) navigate(to, { replace: true });
  }, [location.pathname, location.search, navigate]);

  /** The list query to carry onto the next URL: list keys only. */
  const listQuery = useCallback((overrides = {}) => {
    const sp = new URLSearchParams(searchRef.current);
    for (const key of [...sp.keys()]) {
      if (!LIST_KEYS.includes(key)) sp.delete(key);
    }
    for (const [key, value] of Object.entries(overrides)) {
      if (value === null || value === undefined || value === '') sp.delete(key);
      else sp.set(key, String(value));
    }
    const qs = sp.toString();
    return qs ? `?${qs}` : '';
  }, []);

  const go = useCallback((pathname, search, replace) => {
    if (parsed.mode === 'list' && pathname !== '/quotes' && typeof window !== 'undefined') {
      listScrollByKey.set(location.key, shellScrollTop());
    }
    searchRef.current = search;
    navigate({ pathname, search }, { replace });
  }, [navigate, parsed.mode, location.key]);

  /**
   * Open a record. Record → record replaces (Back returns to the list in one step); so does
   * `{ replace: true }`, which the builder's Save and Cancel use to swap the edit entry for
   * the record it came from (§2.4).
   */
  const openRecord = useCallback((id, { replace } = {}) => {
    go(`/quotes/${id}`, listQuery(), replace ?? parsed.mode === 'record');
  }, [go, listQuery, parsed.mode]);

  const closeRecord = useCallback(() => {
    go('/quotes', listQuery(), false);
  }, [go, listQuery]);

  const editRecord = useCallback((id) => {
    go(`/quotes/${id}/edit`, listQuery(), false);
  }, [go, listQuery]);

  /** New quote. A tab does not ride along: the builder is not inside a tab. */
  const newQuote = useCallback((prefill = {}) => {
    go('/quotes/new', listQuery({
      tab: null,
      customerId: prefill.customerId || null,
      leadId: prefill.leadId || null,
      draft: prefill.draft || null
    }), false);
  }, [go, listQuery]);

  /**
   * Patch the list query and show the list. `null` / default values leave the URL.
   * Replaces unless the patch touches view, page or tab, or `{ push: true }` is passed.
   */
  const setList = useCallback((patch, { push } = {}) => {
    const sp = new URLSearchParams(searchRef.current);
    for (const [key, value] of Object.entries(patch)) {
      const encoded = encode(key, value, defaults);
      if (encoded === null) sp.delete(key);
      else sp.set(key, encoded);
    }
    // A different filter or sort is a different list: it starts on page 1 (S1b), unless the
    // patch names its own page.
    if (!('page' in patch) && Object.keys(patch).some((k) => FILTER_KEYS.includes(k))) {
      sp.delete('page');
    }
    const qs = sp.toString();
    const search = qs ? `?${qs}` : '';
    const shouldPush = push ?? Object.keys(patch).some((k) => PUSH_KEYS.includes(k));
    const onList = parsed.mode === 'list';
    if (onList && search === location.search) return;
    go('/quotes', search, onList ? !shouldPush : false);
  }, [go, parsed.mode, location.search, defaults]);

  /**
   * Another page of the list (S1c). With a record open the record stays open: the URL keeps
   * /quotes/<id> and only `page` changes (push, so Back returns to the previous page). On the
   * list it is setList({ page }).
   */
  const setPage = useCallback((page) => {
    if (parsed.mode !== 'record') {
      setList({ page });
      return;
    }
    const sp = new URLSearchParams(searchRef.current);
    const encoded = encode('page', page, defaults);
    if (encoded === null) sp.delete('page');
    else sp.set('page', encoded);
    const qs = sp.toString();
    go(location.pathname, qs ? `?${qs}` : '', false);
  }, [go, parsed.mode, location.pathname, setList, defaults]);

  /**
   * The List / Pipeline toggle. Saves the choice as this device's default, then always
   * switches the rendered view — also when the URL does not change, which is the common
   * case: the chosen layout is now the default, and defaults stay out of the URL.
   */
  const setLayout = useCallback((layout) => {
    const chosen = layout === 'kanban' ? 'kanban' : 'list';
    updatePrefs((d) => ({ ...d, list: { ...(d.list || {}), layout: chosen } }));
    // The chosen layout is now the default, so it leaves the URL.
    setList({ layout: null });
  }, [setList, updatePrefs]);

  return useMemo(() => ({
    ...parsed,
    openRecord,
    closeRecord,
    editRecord,
    newQuote,
    setList,
    setPage,
    setLayout
  }), [parsed, openRecord, closeRecord, editRecord, newQuote, setList, setPage, setLayout]);
}
